import React, { type FC, useState } from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router'
import { useHostContext } from '@mobi/account/Common/HostContext'
import { useBetAccount, useHostContextFeature } from '@mobi/account/Hooks'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { colors, font } from '@mobi/component-library/Foundations'
import { AlertBox } from '@mobi/component-library/Overlays/AlertBox'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { toMoney } from '@mobi/utils/money'
import { routes as depositLimitRoutes } from './routes'

type HelpTextProps = {
  paymentType: 'deposit' | 'withdrawal'
  inputValue?: number
  paymentLimits: {
    minimum: number
  }
  remainingPaymentAmount: number
  remainingPaymentTransactions: number
  isDisabled?: boolean
  isInfoIconShown?: boolean
  showDepositLimitLink?: boolean
  hasAvailableBalance?: boolean
}

type PaymentLimitAlertBoxProps = {
  isOpen: boolean
  onClose: () => void
  maxDailyAmount: number
}

type DepositLimitAlertBoxProps = PaymentLimitAlertBoxProps & {
  showDepositLimitLink?: boolean
  normalFontWeight?: boolean
}

type InfoIconProps = {
  showDepositLimitLink?: boolean
}

const DepositLimitAlertBox: FC<DepositLimitAlertBoxProps> = ({
  isOpen,
  onClose,
  showDepositLimitLink = true,
  normalFontWeight = false,
  maxDailyAmount,
}) => {
  const navigate = useNavigate()
  const { channel } = useHostContext()
  return (
    <AlertBox
      title='Deposit Limits'
      text={
        <span style={normalFontWeight ? { fontWeight: 'normal' } : undefined}>
          Your remaining daily deposit limit is determined by any personal limit you’ve set OR our
          default limit of {toMoney(maxDailyAmount)} (whichever is lower). You can set a personal
          deposit limit in-app. If you have any questions about your deposit limit, please contact
          us on <CustomerServiceTelephone />
        </span>
      }
      confirmText={showDepositLimitLink ? 'Set Deposit Limit' : undefined}
      confirmColor='primary'
      cancelText='Close'
      cancelColor='secondary_outline'
      isOpen={isOpen}
      onConfirm={() => {
        if (!showDepositLimitLink) return
        if (channel === 'desktop') {
          window.location.href = depositLimitRoutes.editDepositLimit
          return
        }
        navigate(depositLimitRoutes.editDepositLimit)
      }}
      onCancel={onClose}
    />
  )
}

const getPaymentLimitHelpText = ({
  paymentType,
  isPaymentValueVolumeLimitsEnabled,
  inputValue = 0,
  paymentLimits,
  remainingPaymentAmount,
  remainingPaymentTransactions,
  infoIcon,
  hasAvailableBalance = true,
  isWithdrawRealtimeEnabled,
}: Omit<HelpTextProps, 'isDisabled' | 'isInfoIconShown' | 'showDepositLimitLink'> & {
  isPaymentValueVolumeLimitsEnabled: boolean
  infoIcon?: React.ReactNode
  isWithdrawRealtimeEnabled: boolean
}): string | React.ReactNode => {
  if (!isPaymentValueVolumeLimitsEnabled) {
    return inputValue > 0 && inputValue < paymentLimits.minimum
      ? `Minimum $${paymentLimits.minimum.toFixed(2)}`
      : `Enter custom amount`
  }

  if (hasAvailableBalance && (remainingPaymentAmount <= 0 || remainingPaymentTransactions <= 0)) {
    return `Daily ${paymentType} limit reached`
  }

  if (inputValue > remainingPaymentAmount && remainingPaymentAmount > 0) {
    return (
      <span style={{ color: colors.error[600] }}>
        Please reduce your amount to below <strong>{toMoney(remainingPaymentAmount)}</strong>
      </span>
    )
  }

  const minimumAmountText = `Minimum $${paymentLimits.minimum.toFixed(2)}`

  if (paymentType === 'withdrawal') {
    if (isWithdrawRealtimeEnabled) {
      return minimumAmountText // always show minimum as default valid message
    }

    if (inputValue > 0 && inputValue < paymentLimits.minimum) {
      return minimumAmountText
    }

    return `Enter custom amount`
  }

  if (paymentType === 'deposit') {
    if (inputValue > 0 && inputValue < paymentLimits.minimum) {
      return minimumAmountText
    } else {
      return (
        <>
          <strong>{toMoney(remainingPaymentAmount)}</strong> remaining {infoIcon}
        </>
      )
    }
  }

  return ''
}

export const PaymentLimitHelpText: FC<HelpTextProps> = ({
  paymentType,
  inputValue,
  paymentLimits,
  remainingPaymentAmount,
  remainingPaymentTransactions,
  isDisabled = false,
  isInfoIconShown = true,
  showDepositLimitLink = true,
  hasAvailableBalance = true,
}) => {
  const isPaymentValueVolumeLimitsEnabled = useHostContextFeature(
    FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key
  )
  const isWithdrawRealtimeEnabled = useHostContextFeature(FeatureFlags.WITHDRAW_REALTIME.key)
  const { data: betAccount } = useBetAccount()
  const [showInfoModal, setShowInfoModal] = useState(false)

  const infoIcon = isInfoIconShown ? (
    <Icon name='LineInfoCircle' size='1.6rem' onClick={() => setShowInfoModal(true)} />
  ) : undefined

  const helpText = getPaymentLimitHelpText({
    paymentType,
    isPaymentValueVolumeLimitsEnabled,
    inputValue,
    paymentLimits,
    remainingPaymentAmount,
    remainingPaymentTransactions,
    infoIcon,
    isWithdrawRealtimeEnabled,
    hasAvailableBalance,
  })

  return (
    <>
      <TextStyled style={{ color: isDisabled ? colors.neutral[400] : colors.neutral[700] }}>
        {helpText}
      </TextStyled>
      {showInfoModal && paymentType === 'deposit' && (
        <DepositLimitAlertBox
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          showDepositLimitLink={showDepositLimitLink}
          maxDailyAmount={betAccount?.AmlMaxDailyDepositAmount ?? 0}
        />
      )}
    </>
  )
}

export const DepositLimitInfoIcon: FC<InfoIconProps> = ({ showDepositLimitLink = true }) => {
  const { data: betAccount } = useBetAccount()
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <Icon name='LineInfoCircle' size='1.6rem' onClick={() => setShowModal(true)} />
      {showModal && (
        <DepositLimitAlertBox
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          showDepositLimitLink={showDepositLimitLink}
          normalFontWeight
          maxDailyAmount={betAccount?.AmlMaxDailyDepositAmount ?? 0}
        />
      )}
    </>
  )
}

const TextStyled = styled.span({
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  fontFamily: font.family.primary,
  whiteSpace: 'pre',
})
